<template>
    <zen-message-modal
        :is-open="isOpen"
        title="Token created"
        :extra-wide="showMcpSection"
    >
        <p class="text-sm text-taupe-500 mb-4">
            Copy this token now — it will not be shown again.
        </p>
        <div
            class="flex items-center gap-2 rounded-md border border-taupe-200 bg-taupe-50 px-3 py-2 font-mono text-sm break-all"
        >
            <span class="flex-1 select-all">{{ token }}</span>
            <zen-button
                type="button"
                variant="secondary"
                size="small"
                @click="copyToken"
            >
                {{ copied ? 'Copied!' : 'Copy' }}
            </zen-button>
        </div>

        <div class="mt-6 border border-taupe-200 rounded-md">
            <button
                type="button"
                @click="toggleMcpSection"
                aria-controls="mcp-connect-section"
                :aria-expanded="showMcpSection"
                class="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-taupe-800 hover:bg-taupe-50 rounded-md"
            >
                Connect to MCP
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-4 w-4 text-taupe-400 transition-transform"
                    :class="showMcpSection ? 'rotate-90' : ''"
                >
                    <path
                        fill-rule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clip-rule="evenodd"
                    />
                </svg>
            </button>

            <div
                v-show="showMcpSection"
                id="mcp-connect-section"
                class="px-3 pb-3"
            >
                <label
                    for="mcp-client-select"
                    class="block text-sm font-medium text-taupe-800 mb-1"
                >
                    MCP client
                </label>
                <select
                    id="mcp-client-select"
                    v-model="mcpClient"
                    class="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                >
                    <option
                        v-for="client in mcpClients"
                        :key="client.value"
                        :value="client.value"
                    >
                        {{ client.label }}
                    </option>
                </select>

                <p class="mt-4 text-sm font-medium text-taupe-800">
                    Ready-to-paste config
                </p>
                <div
                    class="mt-1 flex items-start gap-2 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                >
                    <pre
                        class="flex-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-taupe-800"
                        >{{ mcpConfigSnippet }}</pre>
                    <zen-button
                        type="button"
                        variant="secondary"
                        size="small"
                        @click="copyMcpConfig"
                    >
                        {{ mcpConfigCopied ? 'Copied!' : 'Copy' }}
                    </zen-button>
                </div>
                <p class="mt-2 text-xs text-taupe-500">
                    Treat this like a credential — don't paste it into a config
                    file you'll commit to source control.
                    <a
                        href="https://zenmanage.com/developers/reference/mcp-server"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sky-600 hover:text-sky-700"
                    >
                        View full setup docs for other clients →
                    </a>
                </p>
            </div>
        </div>

        <template v-slot:buttons>
            <zen-button type="button" variant="primary" @click="$emit('close')">
                Done
            </zen-button>
        </template>
    </zen-message-modal>
</template>

<script>
import { errorMessage } from '@/utils/notifications'

const MCP_SERVER_URL = 'https://mcp.zenmanage.com/mcp'

const MCP_CONFIG_SNIPPET_BUILDERS = {
    'claude-code': (token) =>
        `claude mcp add --transport http zenmanage ${MCP_SERVER_URL} --header "Authorization: Bearer ${token}"`,
    'claude-desktop': (token) =>
        JSON.stringify(
            {
                mcpServers: {
                    zenmanage: {
                        command: 'npx',
                        args: [
                            'mcp-remote',
                            MCP_SERVER_URL,
                            '--transport',
                            'http-only',
                            '--header',
                            'Authorization:${ZENMANAGE_TOKEN}',
                        ],
                        env: { ZENMANAGE_TOKEN: `Bearer ${token}` },
                    },
                },
            },
            null,
            2
        ),
    cursor: (token) =>
        JSON.stringify(
            {
                mcpServers: {
                    zenmanage: {
                        url: MCP_SERVER_URL,
                        headers: { Authorization: `Bearer ${token}` },
                    },
                },
            },
            null,
            2
        ),
    vscode: (token) =>
        JSON.stringify(
            {
                servers: {
                    zenmanage: {
                        type: 'http',
                        url: MCP_SERVER_URL,
                        headers: { Authorization: `Bearer ${token}` },
                    },
                },
            },
            null,
            2
        ),
}

export default {
    name: 'PersonalAccessTokenCreatedModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        token: {
            type: String,
            required: true,
        },
    },
    emits: ['close'],
    data() {
        return {
            copied: false,
            showMcpSection: false,
            mcpClient: 'claude-code',
            mcpConfigCopied: false,
            mcpClients: [
                { value: 'claude-code', label: 'Claude Code' },
                { value: 'claude-desktop', label: 'Claude Desktop' },
                { value: 'cursor', label: 'Cursor' },
                { value: 'vscode', label: 'VS Code / Copilot' },
            ],
        }
    },
    computed: {
        mcpConfigSnippet() {
            if (!this.token) return ''

            const buildSnippet =
                MCP_CONFIG_SNIPPET_BUILDERS[this.mcpClient] ??
                MCP_CONFIG_SNIPPET_BUILDERS.vscode
            return buildSnippet(this.token)
        },
    },
    methods: {
        async copyToken() {
            try {
                await navigator.clipboard.writeText(this.token)
                this.copied = true
                setTimeout(() => {
                    this.copied = false
                }, 2000)
            } catch {
                errorMessage(
                    'Could not copy to clipboard. Please select and copy the token manually.'
                )
            }
        },
        toggleMcpSection() {
            this.showMcpSection = !this.showMcpSection
        },
        async copyMcpConfig() {
            try {
                await navigator.clipboard.writeText(this.mcpConfigSnippet)
                this.mcpConfigCopied = true
                setTimeout(() => {
                    this.mcpConfigCopied = false
                }, 2000)
            } catch {
                errorMessage(
                    'Could not copy to clipboard. Please select and copy the config manually.'
                )
            }
        },
    },
}
</script>
